import { Editor, Transforms } from 'slate'
import { compose, dissoc, filter, prop, values } from 'ramda'
import { useEditor, useSelected, useSlate } from 'slate-react'
import React from 'react'

import { Absolute } from '../../../kiti/components/helpers/Position'
import { Col, Grid } from '../../../kiti/components/structure/Grid'
import { useColors } from '../../../kiti/components/theme/_hooks/colors'
import { useEditorState, useNodeMenuForm } from '../EditorContext'
import BlockPlugin from './BlockPlugin'
import EditorIcon from '../EditorIcon'
import EditorMenuFormWrapper from '../EditorMenuFormWrapper'
import Icon from '../../../kiti/components/shapes/Icon'
import NumberInput from '../../../kiti/components/form/NumberInput'
import Panel from '../../../kiti/components/structure/Panel'

const formatSize = (value, defaultValue) => {
  if (!value) return defaultValue
  return value + 'px'
}

const Element = ({ children, attributes, element, ...props }) => {
  const { setMenu } = useEditorState()
  const colors = useColors()
  const selected = useSelected()
  const { fullImageControl } = useEditor()

  return (
    <div {...attributes} contentEditable={false} style={{ display: 'inline-block', position: 'relative' }}>
      {children}
      <img
        src={element.url}
        style={{
          maxWidth: formatSize(element.max_width, '100%'),
          maxHeight: formatSize(element.max_height, 600),
          boxShadow: selected ? '0 0 0 3px #B4D5FF' : 'none'
        }}
      />
      {fullImageControl && selected && (
        <Absolute top={0.4} right={0.4}>
          <Panel bgColor="dark3" padding={0.6} onClick={() => setMenu('image')}>
            <Icon name="RiPencilLine" color="white" />
          </Panel>
        </Absolute>
      )}
    </div>
  )
}

const insertImageUrlOnContent = (editor, url) => {
  const text = { text: '' }
  const image = { type: 'image', url, children: [text] }
  Transforms.insertNodes(editor, image)
}

const handleInsertImage = (editor, files) => {
  files = compose(filter(prop('size')), values, dissoc('length'))(files)

  if (editor.onInsertImage) {
    editor.setLoading(true)
    editor
      .onInsertImage(files)
      .then(images => images.map(image => insertImageUrlOnContent(editor, image?.src || image)))
      .finally(() => editor.setLoading(false))
  }
}

const PluginIcon = props => {
  const inputRef = React.useRef()
  const editor = useEditor()

  if (!editor.onInsertImage) return false

  const onClick = e => {
    e.preventDefault()
    inputRef.current.click()
  }

  return (
    <div role="button" onMouseDown={onClick}>
      <input
        accept="image/*"
        multiple
        type="file"
        autocomplete="off"
        style={{ display: 'none' }}
        ref={inputRef}
        onChange={e => handleInsertImage(editor, e.target?.files)}
      />
      <EditorIcon icon="RiImageAddLine" {...props} />
    </div>
  )
}

const Menu = () => {
  const editor = useSlate()
  const { menu, setMenu } = useEditorState()
  const [form, setForm, handleChange] = useNodeMenuForm(editor, 'image', true)

  if (menu !== 'image') return false

  const onChange = (key, value) => {
    const [_, at] = Editor.parent(editor, editor.selection)
    Transforms.setNodes(editor, { [key]: value }, { at })
    handleChange(key, value)
  }

  const onKeyPress = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      setMenu()
    }
  }

  return (
    <EditorMenuFormWrapper title="Configuração de imagem">
      <Grid gap={0.5}>
        <Col flex={1}>
          <NumberInput
            onKeyPress={onKeyPress}
            placeholder="Largura máxima (px)"
            size="xsmall"
            value={form.max_width}
            onChange={value => onChange('max_width', value)}
          />
        </Col>

        <Col flex={1}>
          <NumberInput
            onKeyPress={onKeyPress}
            placeholder="Altura máxima (px)"
            size="xsmall"
            value={form.max_height}
            onChange={value => onChange('max_height', value)}
          />
        </Col>

        <Col flex={1}></Col>
      </Grid>
    </EditorMenuFormWrapper>
  )
}

const extend = (onInsertImage, options) => editor => {
  const { fullControl } = options || {}
  const { insertData, isInline, isVoid, markableVoid } = editor

  editor.onInsertImage = onInsertImage
  editor.fullImageControl = fullControl

  editor.isVoid = element => {
    return element.type === 'image' ? true : isVoid(element)
  }

  editor.isInline = element => {
    return element.type === 'image' ? true : isInline(element)
  }

  editor.markableVoid = element => {
    return element.type === 'image' || markableVoid(element)
  }

  editor.insertData = data => {
    const { files } = data
    if (files && files.length > 0) {
      for (const file of files) {
        const reader = new FileReader()
        const [mime] = file.type.split('/')
        if (mime === 'image') {
          reader.addEventListener('load', () => {
            const base64Image = reader.result
            insertImage(editor, base64Image)
          })
          reader.readAsDataURL(file)
        }
      }
    } else {
      insertData(data)
    }
  }

  return editor
}

const insertImage = (editor, url) => {
  const text = { text: '' }
  const image = { type: 'image', url, children: [text] }
  Transforms.insertNodes(editor, image)
  Transforms.insertNodes(editor, {
    type: 'paragraph',
    children: [{ text: '' }]
  })
}

const ImagePlugin = {
  ...BlockPlugin,
  Icon: PluginIcon,
  Element,
  Menu,
  extend
}

export default ImagePlugin
